// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { observer } from "mobx-react-lite";
import React from "react";

import { SARDetailsPresenter } from "../../presenters/SARDetailsPresenter";
import { RISK_LEVEL_KEYS, RISK_LEVELS } from "../OffenderAssessment/constants";
import { CurrentUseFootnote } from "../OffenderAssessment/HistoryCardStyles";
import {
  DRUG_HISTORY_COLUMNS,
  formatLastUseDisplay,
  formatSubstanceName,
  FrequencyOfUseLabels,
  hasCurrentUse,
  MethodOfUseLabels,
  SUBSTANCE_USE_CURRENT_USE_COPY,
} from "../OffenderAssessment/SubstanceUse/constants";
import { shouldShowSexualHistoryContent } from "../OffenderAssessment/utils";
import { useStore } from "../StoreProvider/StoreProvider";
import * as Styled from "./Summary.styles";
import { SummaryEducationEmploymentDetails } from "./SummaryEducationEmploymentDetails";
import { SummaryFamilySocialSupportDetails } from "./SummaryFamilySocialSupportDetails";
import { SummaryOrMissing } from "./SummaryOrMissing";
import { SummarySexualHistoryDetails } from "./SummarySexualHistoryDetails";

interface SummaryOffenderAssessmentProps {
  presenter: SARDetailsPresenter;
}

export const SummaryOffenderAssessment: React.FC<SummaryOffenderAssessmentProps> =
  observer(function SummaryOffenderAssessment({ presenter }) {
    const { activeFeatureVariants } = useStore();
    const sarData = presenter.SARData;
    const {
      domainsWithoutSubstanceUse,
      groupedByRisk,
      offenderAssessmentDisplay,
    } = presenter.offenderAssessment;
    const showRiskProfileSummary =
      presenter.offenderAssessment.shouldShowRiskProfileSummary(
        activeFeatureVariants,
      );
    if (presenter.defendantDeclinedToParticipate) {
      return (
        <Styled.SectionCard>
          <Styled.SectionTitle>Offender Assessment</Styled.SectionTitle>
          <Styled.DetailContainer>
            <Styled.DetailSubsection>
              <Styled.SectionBody>
                <SummaryOrMissing summary={sarData?.defendantStatement} />
              </Styled.SectionBody>
            </Styled.DetailSubsection>
            <Styled.DetailSubsection>
              <Styled.SubsectionTitle>Criminal History</Styled.SubsectionTitle>
              <Styled.SectionBody>
                <SummaryOrMissing summary={sarData?.criminalHistorySummary} />
              </Styled.SectionBody>
            </Styled.DetailSubsection>
          </Styled.DetailContainer>
        </Styled.SectionCard>
      );
    }

    if (!presenter.hasOrasAssessment) {
      return (
        <Styled.SectionCard>
          <Styled.SectionTitle>Offender Assessment</Styled.SectionTitle>
          <Styled.SectionBody>
            <SummaryOrMissing summary={undefined} />
          </Styled.SectionBody>
        </Styled.SectionCard>
      );
    }

    return (
      <Styled.SectionCard>
        <Styled.SectionTitle>Offender Assessment</Styled.SectionTitle>

        {offenderAssessmentDisplay && (
          <Styled.SectionBody>{offenderAssessmentDisplay}</Styled.SectionBody>
        )}

        {!sarData?.ORASDomainsAvailable &&
          activeFeatureVariants["SARManualORAS"] && (
            <Styled.DetailSubsection>
              <Styled.SectionBody>
                <SummaryOrMissing summary={sarData?.noORASDomainReason} />
              </Styled.SectionBody>
            </Styled.DetailSubsection>
          )}

        <Styled.DetailContainer>
          <Styled.DetailSubsection>
            <Styled.SubsectionTitle>
              Substance Abuse History
            </Styled.SubsectionTitle>
            <Styled.SectionBody>
              <div>{sarData?.drugHistorySummary || "None listed"}</div>
              {sarData?.drugHistories && sarData.drugHistories.length > 0 && (
                <Styled.AssessmentTable>
                  <Styled.TableHeaderRow>
                    {DRUG_HISTORY_COLUMNS.map((col) => (
                      <Styled.TableHeaderCell key={col}>
                        {col}
                      </Styled.TableHeaderCell>
                    ))}
                  </Styled.TableHeaderRow>
                  {sarData.drugHistories.map((history) => (
                    <Styled.TableDataRow key={history.id}>
                      <Styled.TableDataCell>
                        {formatSubstanceName(
                          history.substance,
                          history.otherSubstanceName,
                        ) ?? "—"}
                      </Styled.TableDataCell>
                      <Styled.TableDataCell>
                        {history.ageOfRegularUse ?? "—"}
                      </Styled.TableDataCell>
                      <Styled.TableDataCell>
                        {formatLastUseDisplay(
                          history.admitsToCurrentUse,
                          history.lastUse,
                        ) ?? "—"}
                      </Styled.TableDataCell>
                      <Styled.TableDataCell>
                        {history.heaviestUse
                          ? FrequencyOfUseLabels[history.heaviestUse]
                          : "—"}
                      </Styled.TableDataCell>
                      <Styled.TableDataCell>
                        {history.method
                          ? MethodOfUseLabels[history.method]
                          : "—"}
                      </Styled.TableDataCell>
                    </Styled.TableDataRow>
                  ))}
                </Styled.AssessmentTable>
              )}
              {sarData?.drugHistories &&
                hasCurrentUse(sarData.drugHistories) && (
                  <CurrentUseFootnote>
                    {SUBSTANCE_USE_CURRENT_USE_COPY}
                  </CurrentUseFootnote>
                )}
            </Styled.SectionBody>
          </Styled.DetailSubsection>

          {showRiskProfileSummary && (
            <Styled.DetailSubsection>
              <Styled.SubsectionTitle>
                Risk Category Summary
              </Styled.SubsectionTitle>
              <Styled.CategoryRow>
                {RISK_LEVEL_KEYS.map((level) => (
                  <Styled.CategoryColumn key={level}>
                    <Styled.CategoryColumnHeader>
                      Scored {RISK_LEVELS[level]}
                    </Styled.CategoryColumnHeader>
                    <div>
                      {groupedByRisk[level].join(", ") || (
                        <Styled.EmptyState>
                          No domains scored {RISK_LEVELS[level]}
                        </Styled.EmptyState>
                      )}
                    </div>
                  </Styled.CategoryColumn>
                ))}
              </Styled.CategoryRow>
            </Styled.DetailSubsection>
          )}

          {domainsWithoutSubstanceUse.map((domain) => {
            const summary =
              presenter.offenderAssessment.getDomainSummary(domain);
            const showSexualHistory =
              domain.key === "responsivity" &&
              shouldShowSexualHistoryContent(
                sarData?.involvesSexCrime,
                activeFeatureVariants,
              );

            // Content shown below this domain's own summary, if any. Whether
            // the summary itself gets a "Summary:" label is derived from
            // whether this exists, rather than tracked separately.
            let extraContent: React.ReactNode = null;
            if (domain.key === "educationEmployment") {
              extraContent = (
                <SummaryEducationEmploymentDetails presenter={presenter} />
              );
            } else if (domain.key === "familySocialSupport") {
              extraContent = (
                <SummaryFamilySocialSupportDetails presenter={presenter} />
              );
            } else if (showSexualHistory) {
              extraContent = (
                <SummarySexualHistoryDetails presenter={presenter} />
              );
            }

            return (
              <Styled.DetailSubsection key={domain.key}>
                <Styled.SubsectionTitle>{domain.title}</Styled.SubsectionTitle>
                <Styled.SectionBody>
                  <SummaryOrMissing
                    summary={summary}
                    labeled={extraContent !== null}
                  />
                  {extraContent}
                </Styled.SectionBody>
              </Styled.DetailSubsection>
            );
          })}
        </Styled.DetailContainer>
      </Styled.SectionCard>
    );
  });
